/** Tactical-board adapter over the shared Resource command/receipt lifetime. */
import type { DraftContext } from '@/features/coach-review/composerDrafts';
import { hasBoardEvidenceChanges } from '@/features/coach-review/reviewChanges';
import { sceneFromOnDisk, sceneToOnDisk, validateScene, type BoardScene } from '@/lib/canvas/board-schema';
import { feedbackProseLength } from '@/lib/feedbackAuthoringContinuation';
import type { ResourceEditorReturnTarget } from '@/lib/resourceEditorContinuation';
import type { ResourceCommand, ResourceDetailResponse } from '@/lib/types';
import { createResourceEditorSession, type ResourceEditorAdapter, type ResourceEditorInitial } from './resourceEditorSessionCore';
export type { ResourceEditorLinkTarget } from './resourceEditorSessionCore';
type BoardValue={scene:BoardScene;label:string};
function boardValue(detail:ResourceDetailResponse):BoardValue {
  const item=detail.resource;
  if (item.availability!=='available' || item.kind!=='tactical_board') throw new Error('resource_editor_unavailable');
  const parsed=sceneFromOnDisk(item.payload.scene);
  if (!parsed.ok) throw new Error('resource_editor_invalid_scene');
  // Editing the current Resource keeps its mutable Library name. Historical
  // and read-only selections still describe their exact immutable revision.
  const label=detail.editor?.can_edit===true && detail.editor.current_revision_id===item.revision_id
    ? item.library_label ?? item.display_label : item.display_label;
  return {scene:parsed.value,label};
}
function commandValue(command:ResourceCommand):BoardValue|null {
  if (command.action!=='save' && command.action!=='create') return null;
  if (command.body.kind!=='tactical_board' || !('scene' in command.body.payload)) throw new Error('resource_editor_invalid_scene');
  const parsed=sceneFromOnDisk(command.body.payload.scene);
  if (!parsed.ok) throw new Error('resource_editor_invalid_scene');
  return {scene:parsed.value,label:command.body.label ?? ''};
}


const adapter:ResourceEditorAdapter<BoardValue>={
  kind:'tactical_board',fromDetail:boardValue,fromCommand:commandValue,
  validate(value){const parsed=validateScene(value.scene);
    if(!parsed.ok||feedbackProseLength(value.label)>160)throw new Error('resource_editor_invalid_scene');
    return {scene:parsed.value,label:value.label};},
  changed:(a,b)=>hasBoardEvidenceChanges(a.scene,b.scene)||a.label!==b.label,
  payload:value=>({schema_version:1,scene:sceneToOnDisk(value.scene)}),
};
export function createResourceBoardSession(context:DraftContext,entryId:string,initial:ResourceEditorInitial<BoardValue>,
  returnTarget:ResourceEditorReturnTarget={kind:'standalone'}){
  return createResourceEditorSession(context,entryId,initial,adapter,returnTarget);
}
export type ResourceBoardSession=ReturnType<typeof createResourceBoardSession>;
